import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import { Button } from '../common';
import PropTypes from 'prop-types';

const MakeupEventModal = ({ isOpen, onClose, originalEvent, onSave, tutors, courses }) => {
  const [suggestedTime, setSuggestedTime] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && originalEvent) {
      // For the suggested date, suggest the next week at the same time
      const originalStartTime = new Date(originalEvent.start);
      const originalEndTime = new Date(originalEvent.end);
      const duration = originalEndTime.getTime() - originalStartTime.getTime();
      
      const suggestedStartTime = new Date(originalStartTime);
      suggestedStartTime.setDate(suggestedStartTime.getDate() + 7); // One week later
      
      const suggestedEndTime = new Date(suggestedStartTime.getTime() + duration);
      
      setSuggestedTime(suggestedStartTime);
      setStartTime(suggestedStartTime);
      setEndTime(suggestedEndTime);
      
      // Set default title and description
      setTitle(`Makeup: ${originalEvent.title}`);
      setDescription(`This is a makeup event for the cancelled class on ${originalStartTime.toLocaleDateString()}.`);
    }
  }, [isOpen, originalEvent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!startTime || !endTime) {
      setError('Start time and end time are required');
      return;
    }
    
    if (endTime <= startTime) {
      setError('End time must be after start time');
      return;
    }
    
    try {
      setSaving(true);
      
      // Create the makeup event
      const token = localStorage.getItem('token');
      
      // Ensure proper date formatting
      const formattedStartTime = startTime instanceof Date ? startTime.toISOString() : null;
      const formattedEndTime = endTime instanceof Date ? endTime.toISOString() : null;
      
      if (!formattedStartTime || !formattedEndTime) {
        setError('Invalid date format. Please select valid dates.');
        setSaving(false);
        return;
      }
      
      console.log('Sending dates:', {
        start: formattedStartTime,
        end: formattedEndTime
      });
      
      const response = await axios.post(
        `/api/calendar/${originalEvent.id}/schedule-makeup`,
        {
          start_time: formattedStartTime,
          end_time: formattedEndTime,
          title,
          description,
          course_id: originalEvent.course_id,
          tutor_id: originalEvent.tutor_id,
          country: originalEvent.country,
          location: originalEvent.location,
          event_type: originalEvent.event_type || 'course',
          is_makeup: true
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': token
          }
        }
      );
      
      console.log('Makeup event created successfully:', response.data);
      
      // Call the onSave callback with the response data
      onSave(response.data);
      
      // Close the modal
      onClose();
      
      // Force calendar to refresh to show the new event
      window.location.reload();
    } catch (error) {
      console.error('Error scheduling makeup event:', error);
      
      // Check if it's an axios error with response data
      if (error.response && error.response.data) {
        console.log('Server error response:', error.response.data);
        setError(error.response.data.message || 'Server error while scheduling makeup event');
      } else if (error.request) {
        // The request was made but no response was received
        console.log('No response received:', error.request);
        setError('No response from server. The event may have been created.');
      } else {
        // Something happened in setting up the request
        setError('Failed to schedule makeup event: ' + error.message);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4 overflow-hidden">
        <div className="bg-blue-600 text-white py-4 px-6">
          <h3 className="text-lg font-medium">Schedule Makeup Event</h3>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-2 bg-red-100 border-l-4 border-red-500 text-red-700">
              <p>{error}</p>
            </div>
          )}
          
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">
              Scheduling a makeup for the cancelled event:
            </p>
            <p className="font-medium text-gray-800">{originalEvent?.title}</p>
            <p className="text-sm text-gray-600">
              Original date: {originalEvent ? new Date(originalEvent.start).toLocaleString() : ''}
            </p>
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Event Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-2 border rounded"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Start Time
            </label>
            <DatePicker
              selected={startTime}
              onChange={date => {
                setStartTime(date);
                
                // Maintain the same duration
                if (endTime && originalEvent) {
                  const originalStartTime = new Date(originalEvent.start);
                  const originalEndTime = new Date(originalEvent.end);
                  const duration = originalEndTime.getTime() - originalStartTime.getTime();
                  
                  setEndTime(new Date(date.getTime() + duration));
                }
              }}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="MMMM d, yyyy h:mm aa"
              className="w-full p-2 border rounded"
              minDate={new Date()}
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              End Time
            </label>
            <DatePicker
              selected={endTime}
              onChange={date => setEndTime(date)}
              showTimeSelect
              timeFormat="HH:mm"
              timeIntervals={15}
              timeCaption="Time"
              dateFormat="MMMM d, yyyy h:mm aa"
              className="w-full p-2 border rounded"
              minDate={startTime || new Date()}
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full p-2 border rounded"
              rows={3}
            />
          </div>
          
          <div className="flex justify-end mt-6">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="mr-2"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving}
            >
              {saving ? 'Scheduling...' : 'Schedule Makeup Event'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

MakeupEventModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  originalEvent: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  tutors: PropTypes.array,
  courses: PropTypes.array
};

export default MakeupEventModal;